import { Box, Grid, MenuItem, Typography } from '@material-ui/core'
import React, { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { theme } from '../../../../theme'
import { validators } from '../../../../validation/validators'
import {
  areaUnit,
  hourUnit,
  lengthUnit,
  powerPer1000Unit,
  powerPerLengthUnit,
  powerUnit,
} from '../../../../util/formatters'
import {
  EnergySystemType,
  ScheduleFragment,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
} from '../../../../generated/graphql'
import {
  InputTabFormValues,
  saveInputData,
  updateModel,
  useInputTabStyles,
} from './input-util'
import { ScheduleHoursRow, ScheduleMonthsRow } from './InputScheduleComponents'

type Props = {
  id: string
  schedules: ScheduleFragment[]
  defaultValues: InputTabFormValues | undefined
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export function CalibrationInputDhwTab({
  id,
  schedules,
  defaultValues,
  onUpdate,
  onDbUpdate,
}: Props): React.ReactElement {
  const classes = useInputTabStyles()
  const { t } = useTranslation()

  const { control, errors, reset, formState } = useForm<InputTabFormValues>({
    defaultValues,
    mode: 'onBlur',
  })

  const dbUpdateNeededRef = useRef(false)

  const updateModelData = async (values: InputTabFormValues) => {
    await updateModel({
      values,
      defaultValues: defaultValues ?? ({} as InputTabFormValues),
      esType: EnergySystemType.DomesticHotWater,
      control,
      formState,
      updateStateRef: dbUpdateNeededRef,
      onReset: reset,
      onUpdate,
    })
  }

  const saveChanges = async () => {
    await saveInputData({ energySystemId: id, control, onDbUpdate })
  }

  useEffect(() => {
    void updateModelData(defaultValues ?? ({} as InputTabFormValues))

    return () => {
      if (dbUpdateNeededRef.current && defaultValues) {
        void saveChanges()
        dbUpdateNeededRef.current = false
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form style={{ marginTop: theme.spacing(2) }}>
      <Box flexGrow={1} className={classes.inputTab}>
        <Typography>{t('energySystem.parameters.dhw.tabs.general')}</Typography>
        <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.parameters.domesticHotWater.consumptionDensity"
              error={
                errors.values?.parameters?.domesticHotWater?.consumptionDensity
              }
              label={t('energySystem.parameters.dhw.consumption')}
              rules={{
                ...validators.required,
                ...validators.copModifier,
              }}
              size="small"
              onBlur={() => updateModelData(control.getValues())}
              InputProps={{
                endAdornment: (
                  <Typography>
                    {powerUnit}
                    {hourUnit}/{areaUnit}
                  </Typography>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.parameters.domesticHotWater.distributionEfficiency"
              error={
                errors.values?.parameters?.domesticHotWater
                  ?.distributionEfficiency
              }
              label={t('energySystem.parameters.dhw.distributionEfficiency')}
              rules={{
                ...validators.required,
                ...validators.efficiency,
              }}
              size="small"
              onBlur={() => updateModelData(control.getValues())}
            />
          </Grid>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.parameters.domesticHotWater.consumptionFactor"
              error={
                errors.values?.parameters?.domesticHotWater?.consumptionFactor
              }
              label={t('energySystem.parameters.dhw.consumptionFactor')}
              rules={{
                ...validators.required,
                ...validators.copModifier,
              }}
              size="small"
              onBlur={() => updateModelData(control.getValues())}
            />
          </Grid>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.scheduleId"
              error={errors.values?.scheduleId}
              label={t('energySystem.parameters.dhw.consumptionProfile')}
              rules={validators.required}
              size="small"
              disabled
              select
            >
              {Object.values(schedules).map((schedule) => (
                <MenuItem key={schedule.id} value={schedule.id}>
                  {schedule.name}
                </MenuItem>
              ))}
            </ControlledTextField>
          </Grid>
        </Grid>

        <ScheduleHoursRow
          control={control}
          errors={errors}
          onUpdateModel={updateModelData}
        />

        <ScheduleMonthsRow
          control={control}
          errors={errors}
          onUpdateModel={updateModelData}
        />
      </Box>
      <Box
        flexGrow={1}
        className={classes.inputTab}
        style={{ marginTop: theme.spacing(2) }}
      >
        <Typography>
          {t('energySystem.parameters.dhw.tabs.hotWaterCirculation')}
        </Typography>
        <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.parameters.domesticHotWater.specificLength"
              error={
                errors.values?.parameters?.domesticHotWater?.specificLength
              }
              label={t('energySystem.parameters.dhw.hwcSpecificLenght')}
              rules={{
                ...validators.required,
                ...validators.copModifier,
              }}
              size="small"
              onBlur={() => updateModelData(control.getValues())}
              InputProps={{
                endAdornment: (
                  <Typography>
                    {lengthUnit}/{areaUnit}
                  </Typography>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.parameters.domesticHotWater.specificHeatLoss"
              error={
                errors.values?.parameters?.domesticHotWater?.specificHeatLoss
              }
              label={t('energySystem.parameters.dhw.hwcSpecificHeatLoss')}
              rules={{
                ...validators.required,
                ...validators.copModifier,
              }}
              size="small"
              onBlur={() => updateModelData(control.getValues())}
              InputProps={{
                endAdornment: <Typography>{powerPerLengthUnit}</Typography>,
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.parameters.domesticHotWater.heatersAmount"
              error={errors.values?.parameters?.domesticHotWater?.heatersAmount}
              label={t('energySystem.parameters.dhw.hotWaterHeaterAmount')}
              rules={{
                ...validators.required,
                ...validators.nonNegativeInteger,
              }}
              size="small"
              onBlur={() => updateModelData(control.getValues())}
            />
          </Grid>
          <Grid item xs={3}>
            <ControlledTextField
              control={control}
              name="values.parameters.domesticHotWater.heaterPower"
              error={errors.values?.parameters?.domesticHotWater?.heaterPower}
              label={t('energySystem.parameters.dhw.hotWaterHeaterPower')}
              rules={{
                ...validators.required,
                ...validators.copModifier,
              }}
              size="small"
              onBlur={() => updateModelData(control.getValues())}
              InputProps={{
                endAdornment: (
                  <Typography>
                    {powerPer1000Unit}/{t('common.amountUnit')}
                  </Typography>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </form>
  )
}
